/* Public self-justification page — iter88.
 * Route: /justify-absence/:token (no auth) — employees click the link in
 * the J-2 email/SMS and submit their justification. */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { CheckCircle, Warning, ClipboardText } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicSelfJustify() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [reason, setReason] = useState('');
  const [attachment, setAttachment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/public/staff-absence/${token}`);
        setInfo(data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Lien invalide ou expiré');
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/public/staff-absence/${token}/justify`,
        { reason, attachment_url: attachment });
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur enregistrement');
    } finally { setSubmitting(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-3"
                          data-testid="pj-error-card">
            <Warning size={48} className="mx-auto text-red-600" weight="duotone" />
            <h1 className="text-xl font-outfit font-bold">Lien inaccessible</h1>
            <p className="text-sm text-[#64748B]">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (!info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <p className="text-[#94A3B8]">Chargement…</p>
      </div>
    );
  }
  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-3"
                          data-testid="pj-success-card">
            <CheckCircle size={56} weight="duotone" className="mx-auto text-emerald-600" />
            <h1 className="text-2xl font-outfit font-bold text-emerald-700">
              Justification envoyée
            </h1>
            <p className="text-sm text-[#0F172A]">
              Votre justification pour le <strong>{info.date}</strong> a été
              transmise. La RH vous répondra sous 48 heures.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-start justify-center bg-[#F8FAFC] p-4">
      <Card className="max-w-lg w-full mt-8" data-testid="pj-form-card">
        <CardContent className="p-6 space-y-4">
          <div className="text-center">
            <ClipboardText size={40} weight="duotone" className="mx-auto text-[#0052FF]" />
            <h1 className="text-2xl font-outfit font-bold mt-2">
              Justifier mon absence
            </h1>
          </div>
          <div className="p-3 rounded bg-[#EEF2FF] border border-[#C7D2FE] text-sm">
            <p><strong>Employé :</strong> {info.employee_name}</p>
            <p><strong>Absence sélectionnée :</strong> {info.date} ({info.hours_missed}h)</p>
          </div>

          {info.month_absences && info.month_absences.length > 1 && (
            <div className="border border-[#E2E8F0] rounded p-3 space-y-2"
                    data-testid="pj-month-history">
              <p className="text-xs font-bold text-[#0F172A] uppercase">
                Vos absences ce mois-ci ({info.month})
              </p>
              <ul className="space-y-1 text-xs">
                {info.month_absences.map((a) => (
                  <li key={a.id}
                    className="flex items-center justify-between p-2 rounded bg-[#F8FAFC] border border-[#E2E8F0]">
                    <span>
                      <strong>{a.date}</strong> — {a.hours_missed}h
                      {' '}
                      <span className={
                        a.status === 'justified' ? 'text-emerald-700' :
                        a.status === 'rejected' ? 'text-red-700' : 'text-amber-700'
                      }>({a.status})</span>
                      {a.already_submitted && (
                        <span className="ml-1 text-[10px] text-blue-600">
                          · justif. envoyée
                        </span>
                      )}
                    </span>
                    {a.id !== token && a.status === 'pending' && (
                      <a href={`/justify-absence/${a.token}`}
                        className="text-[#0052FF] hover:underline">
                        Justifier →
                      </a>
                    )}
                  </li>
                ))}
              </ul>
              <p className="text-[10px] text-[#94A3B8]">
                Vous pouvez justifier plusieurs jours en cliquant sur "Justifier →"
                pour chaque ligne.
              </p>
            </div>
          )}
          {info.already_submitted && (
            <div className="p-2 text-xs bg-amber-50 border border-amber-200 rounded text-amber-800">
              Vous avez déjà soumis une justification. Nouveau formulaire = mise à jour.
            </div>
          )}
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Motif *</Label>
              <Textarea rows={4} value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                placeholder="Ex. Consultation médicale, mission externe, formation…"
                data-testid="pj-reason" />
            </div>
            <div>
              <Label>Pièce jointe (URL certificat, PDF…)</Label>
              <Input value={attachment}
                onChange={(e) => setAttachment(e.target.value)}
                placeholder="https://…"
                data-testid="pj-attachment" />
            </div>
            <Button type="submit" disabled={submitting || !reason.trim()}
              className="w-full bg-[#0052FF] hover:bg-[#0044CC] text-white h-11"
              data-testid="pj-submit">
              {submitting ? 'Envoi…' : '✓ Envoyer la justification'}
            </Button>
            <p className="text-[10px] text-center text-[#94A3B8]">
              Lien personnel valable 5 jours. Aucun compte requis.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
